import { Pic } from "./Pic";

import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { scrollToId } from "@/lib/scroll";
import { useT } from "@/i18n";
import { HouseSystems } from "./HouseSystems";

export const MODELS = [
  { id: "01", price: 60, img: "/img/emma/modello-60.jpg" },
  { id: "02", price: 100, img: "/img/emma/modello-100.jpg" },
  { id: "03", price: 160, img: "/img/emma/modello-160.jpg" },
  { id: "04", price: 200, img: "/img/emma/modello-200a.jpg" },
  { id: "05", price: 200, img: "/img/emma/modello-200b.jpg" },
  { id: "06", price: 250, img: "/img/emma/modello-250.jpg" },
  { id: "07", price: 300, img: "/img/emma/modello-300.jpg" },
  { id: "08", price: 450, img: "/img/emma/modello-450.jpg" },
];

export const askModel = (m, message) => {
  window.dispatchEvent(new CustomEvent("prefill-quote", {
    detail: { project_type: "Casa in legno", message: message ?? `Sono interessato al Modello ${m.id} (a partire da ${m.price}.000 €).` },
  }));
  scrollToId("#contatti");
};

const SIZES = [
  { id: "S", tag: "Compatta", desc: "Essenziale e luminosa: ideale per una coppia, per iniziare o come seconda casa.", sketch: "/img/emma/schizzo-s.jpg", models: ["01", "02"] },
  { id: "M", tag: "Familiare", desc: "Lo spazio giusto per la famiglia che cresce, con zona giorno aperta e camere separate.", sketch: "/img/emma/schizzo-m.jpg", models: ["03", "04"] },
  { id: "L", tag: "Spaziosa", desc: "Ambienti ampi, grandi vetrate e spazi esterni che diventano parte della casa.", sketch: "/img/emma/schizzo-l.jpg", models: ["05", "06"] },
  { id: "XL", tag: "Villa", desc: "La villa in legno su misura, su più livelli, pensata senza compromessi.", sketch: "/img/emma/schizzo-xl.jpg", models: ["07", "08"] },
];

const Variant = ({ m, s, n, t }) => {
  const name = `${t("models.casa", "Casa")} ${s.id} · ${n}`;
  const msg = t("models.msgSize", "Sono interessato alla {name} (a partire da {price}.000 €).").replace("{name}", name).replace("{price}", m.price);
  return (
    <li className="group flex items-center gap-4 border-t border-[#121110]/15 py-4" data-testid={`model-card-${m.id}`}>
      <Pic src={m.img} alt={`${t("models.alt", "Casa in legno")} ${name}`} loading="lazy" className="h-20 w-28 shrink-0 object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="min-w-0 flex-1">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#6E685E]">{name}</p>
        <p className="mt-1 font-display text-2xl font-bold tracking-tight" data-testid={`model-price-${m.id}`}>
          <span className="mr-2 block text-sm font-normal text-[#6E685E] sm:inline">{t("models.from", "A partire da")}</span><span className="whitespace-nowrap">€ {m.price}.000</span>
        </p>
      </div>
      <button onClick={() => askModel(m, msg)} className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-[#121110]/20 transition-[background-color,transform] duration-500 hover:rotate-45 hover:bg-[#C26D38]" aria-label={`${t("models.ask", "Richiedi preventivo")} ${name}`} data-testid={`model-quote-btn-${m.id}`}>
        <ArrowUpRight className="h-5 w-5" />
      </button>
    </li>
  );
};

const SizeTabs = ({ cur, setCur }) => (
  <div className="flex flex-wrap gap-2" role="tablist">
    {SIZES.map((s) => (
      <button key={s.id} role="tab" aria-selected={cur.id === s.id} onClick={() => setCur(s)}
        className={`flex h-14 min-w-[4.5rem] items-center justify-center rounded-full border px-5 font-display text-xl font-bold transition-[background-color,color,border-color] duration-300 ${cur.id === s.id ? "border-[#121110] bg-[#121110] text-[#F7F5F0]" : "border-[#121110]/20 hover:border-[#121110]"}`}
        data-testid={`model-size-tab-${s.id}`}>
        {s.id}
      </button>
    ))}
  </div>
);

const ModelCollection = () => {
  const t = useT();
  const [cur, setCur] = useState(SIZES[0]);
  const models = cur.models.map((id) => MODELS.find((m) => m.id === id));
  return (
    <div className="mt-10" data-testid="models-collection">
      <SizeTabs cur={cur} setCur={setCur} />
      <motion.div key={cur.id} initial={{ opacity: 0.4, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-14" data-testid={`model-size-panel-${cur.id}`}>
        <figure className="relative lg:col-span-7">
          <div className="overflow-hidden bg-[#EFEAE0] p-3 shadow-[0_30px_60px_-30px_rgba(18,17,16,0.35)] sm:p-5">
            <Pic src={cur.sketch} alt={`${t("models.sketchAlt", "Schizzo a mano della")} ${t("models.casa", "Casa")} ${cur.id}`} className="aspect-[3/2] w-full object-cover mix-blend-multiply" data-testid="model-sketch" />
          </div>
          <figcaption className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-[#6E685E]">{t("models.sketchCap", "Schizzo di progetto · ogni casa si personalizza")}</figcaption>
          <span className="pointer-events-none absolute -top-8 right-4 select-none font-display text-[7rem] font-extrabold leading-none text-[#C26D38]/90 sm:text-[9rem]" style={{ fontStretch: "75%" }} aria-hidden="true">{cur.id}</span>
        </figure>
        <div className="lg:col-span-5">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C26D38]">{t(`models.size.${cur.id}.tag`, cur.tag)}</p>
          <h3 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-testid="model-size-title">{t("models.casa", "Casa")} {cur.id}</h3>
          <p className="mt-5 text-base leading-relaxed text-[#4D4943] sm:text-lg">{t(`models.size.${cur.id}.desc`, cur.desc)}</p>
          <ul className="mt-8 border-b border-[#121110]/15">
            {models.map((m, n) => <Variant key={m.id} m={m} s={cur} n={n + 1} t={t} />)}
          </ul>
        </div>
      </motion.div>
    </div>
  );
};

export const Models = () => {
  const t = useT();
  return (
  <section id="modelli" className="bg-[#F7F5F0] px-5 py-28 text-[#121110] sm:px-8 lg:py-40" data-testid="models-section">
    <div className="mx-auto max-w-[1500px]">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-8">
          <SectionLabel index="01">{t("models.label", "Case in legno · Modelli")}</SectionLabel>
          <MaskLines
            className="mt-10 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.2rem]"
            lines={[t("models.h1", "Tu scegli il budget,"), <span key="r">{t("models.h2a", "noi facciamo")} <em className="font-serif font-normal text-[#C26D38]">{t("models.h2em", "il resto.")}</em></span>]}
          />
        </div>
        <Reveal className="text-base leading-relaxed text-[#6E685E] sm:text-lg lg:col-span-4">
          {t("models.text", "Case prefabbricate a telaio o in X-Lam, chiavi in mano. Scegli il modello più vicino alla tua idea: lo personalizziamo insieme.")}
        </Reveal>
      </div>
      <HouseSystems />
      <p className="mt-24 font-mono text-xs uppercase tracking-[0.25em] text-[#C26D38]" data-testid="models-grid-label">{t("models.gridLabel2", "La collezione · scegli la tua misura")}</p>
      <ModelCollection />
    </div>
  </section>
  );
};
